/**
 * Agent API service for direct communication with the baseline agent.
 *
 * This service replaces Flask backend dependencies, enabling the basic
 * frontend to communicate directly with the agent for:
 * - User identity (config-based, no login)
 * - Conversation management
 * - Memory overrides
 *
 * Blueprint: specs/019-clean-ui-no-api/contracts/openapi.yaml
 */

// User type for config-based identity (simplified from Flask backend)
export interface AgentUser {
  id: string;
  name: string;
  org_id: string;
  org_name: string;
}

// Conversation types matching agent API
export interface AgentConversationSummary {
  id: string;
  title: string;
  created_at: string;
  updated_at: string;
}

export interface AgentMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: string;
}

export interface AgentConversationDetail extends AgentConversationSummary {
  messages: AgentMessage[];
}

export interface CreateConversationRequest {
  title?: string;
}

// Memory override types
export type MemoryOverrideAction = 'create' | 'update' | 'delete' | 'restore';

export interface MemoryOverrideRequest {
  action: MemoryOverrideAction;
  memory_id: string;
  content?: string;
}

export interface MemoryOverrideResponse {
  status: 'created' | 'updated' | 'deleted' | 'restored';
  override_id?: string;
  memory_id: string;
}

// Error type
export interface AgentApiError {
  error: string;
  detail?: string;
}

const AGENT_BASE =
  process.env.REACT_APP_AGENT_URL || 'http://localhost:8090';

class AgentApiService {
  private async request<T>(
    endpoint: string,
    options?: RequestInit,
  ): Promise<T> {
    const { getAuthToken } = await import('../context/AuthContext');
    const token = getAuthToken();
    const authHeaders: Record<string, string> = token
      ? { Authorization: `Bearer ${token}` }
      : {};

    const response = await fetch(`${AGENT_BASE}${endpoint}`, {
      headers: {
        'Content-Type': 'application/json',
        ...authHeaders,
        ...options?.headers,
      },
      ...options,
    });

    // Clear stale token and redirect to login on 401
    if (response.status === 401) {
      localStorage.removeItem('cxs_token');
      localStorage.removeItem('cxs_user');
      window.location.href = '/login';
      throw new Error('Unauthorized');
    }

    if (!response.ok) {
      let errorMessage = 'Request failed';
      try {
        const error: AgentApiError = await response.json();
        errorMessage = error.error || error.detail || errorMessage;
      } catch {
        // Ignore JSON parse errors
      }
      throw new Error(errorMessage);
    }

    // Handle 204 No Content
    if (response.status === 204) {
      return undefined as T;
    }

    return response.json();
  }

  // =========================================================================
  // User (T013)
  // =========================================================================

  /**
   * Get the configured user identity.
   * No authentication required - single-user mode.
   */
  async getUser(): Promise<AgentUser> {
    return this.request<AgentUser>('/user');
  }

  // =========================================================================
  // Conversations (T014, T028, T029, T030)
  // =========================================================================

  /**
   * Create a new conversation.
   * @param title Optional title (auto-generated if not provided)
   */
  async createConversation(
    title?: string,
  ): Promise<AgentConversationDetail> {
    const body = title ? { title } : undefined;
    return this.request<AgentConversationDetail>('/conversations', {
      method: 'POST',
      body: body ? JSON.stringify(body) : undefined,
    });
  }

  /**
   * List all conversations for the configured user.
   * Sorted by updated_at descending.
   */
  async listConversations(): Promise<AgentConversationSummary[]> {
    return this.request<AgentConversationSummary[]>('/conversations');
  }

  /**
   * Get a conversation with its message history.
   * @param conversationId Conversation identifier
   */
  async getConversation(
    conversationId: string,
  ): Promise<AgentConversationDetail> {
    return this.request<AgentConversationDetail>(
      `/conversations/${conversationId}`,
    );
  }

  /**
   * Delete a conversation (hard delete).
   * Removes all metadata, messages, state, and plans.
   * @param conversationId Conversation identifier
   */
  async deleteConversation(conversationId: string): Promise<void> {
    await this.request(`/conversations/${conversationId}`, {
      method: 'DELETE',
    });
  }

  // =========================================================================
  // Memory Overrides (T042)
  // =========================================================================

  /**
   * Create, update, delete, or restore a memory override.
   * @param request Memory override request
   */
  async upsertMemoryOverride(
    request: MemoryOverrideRequest,
  ): Promise<MemoryOverrideResponse> {
    return this.request<MemoryOverrideResponse>('/memory-overrides', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }
}

export const agentApi = new AgentApiService();
